import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { DayPicker } from 'react-day-picker';
import type { DayButtonProps } from 'react-day-picker';
import 'react-day-picker/style.css';
import { useEventsForMonth, useUpcomingEvents } from '../hooks/useEvents';
import { useUpcomingReservations } from '../hooks/useReservations';
import { useLocation } from '../lib/location';
import type { CantoEvent, Reservation } from '../api/types';
import { DayColumn, sod, eod, eventOnDay } from '../components/EventCard';
import '../styles/events.css';

// ---------------------------------------------------------------------------
// Constants (calendar dot colors — used by CalendarDayButton)
// ---------------------------------------------------------------------------

const EVENT_TYPE_CLASS: Record<string, string> = {
  sporting_event: 'events-type-sporting-event',
  concert: 'events-type-concert',
  party: 'events-type-party',
  beer_release: 'events-type-beer-release',
  festival: 'events-type-festival',
  convention: 'events-type-convention',
  private_event: 'events-type-private-event',
  other: 'events-type-other',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function toDateKey(date: Date): string {
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

// ---------------------------------------------------------------------------
// Context — passes event map into custom DayButton
// ---------------------------------------------------------------------------

const EventDayContext = createContext<Map<string, CantoEvent[]>>(new Map());

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

type Tab = 'upcoming' | 'calendar';

export function EventsPage(): React.ReactElement {
  const { locationId } = useLocation();
  const [tab, setTab] = useState<Tab>('upcoming');
  const today = useMemo(() => new Date(), []);
  const [calendarMonth, setCalendarMonth] = useState<Date>(today);
  const [selectedDay, setSelectedDay] = useState<Date | undefined>(undefined);

  return (
    <div className="canto-page canto-page-narrow events-page">
      <div className="canto-page-header">
        <h1>Events</h1>
      </div>

      {/* Tab toggle */}
      <div className="seg-ctrl events-tablist" role="tablist">
        <button
          role="tab"
          className={`seg-btn${tab === 'upcoming' ? ' active' : ''}`}
          onClick={() => setTab('upcoming')}
        >
          Next 7 days
        </button>
        <button
          role="tab"
          className={`seg-btn${tab === 'calendar' ? ' active' : ''}`}
          onClick={() => setTab('calendar')}
        >
          Calendar
        </button>
      </div>

      {tab === 'upcoming' ? (
        <UpcomingView locationId={locationId} today={today} />
      ) : (
        <CalendarView
          locationId={locationId}
          month={calendarMonth}
          onMonthChange={setCalendarMonth}
          selectedDay={selectedDay}
          onSelectDay={setSelectedDay}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Upcoming — 7-day gallery with DayColumn cards
// ---------------------------------------------------------------------------

interface UpcomingViewProps {
  locationId: string | null;
  today: Date;
}

function UpcomingView({ locationId, today }: UpcomingViewProps): React.ReactElement {
  const { data, isLoading } = useUpcomingEvents(locationId, 7);
  const { data: resData } = useUpcomingReservations(locationId, 7);
  const items = data?.items ?? [];
  const reservationItems: Reservation[] = resData?.items ?? [];

  const days = useMemo(
    () =>
      Array.from({ length: 7 }, (_, i) => {
        const d = new Date(today);
        d.setDate(today.getDate() + i);
        return { date: d, start: sod(d), end: eod(d) };
      }),
    [today],
  );

  const dayEvents = useMemo(
    () =>
      days.map((d) =>
        items
          .filter((ev) => eventOnDay(ev, d.start, d.end))
          .sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime()),
      ),
    [days, items],
  );

  const dayReservations = useMemo(
    () =>
      days.map((d) =>
        reservationItems
          .filter((r) => {
            const t = new Date(r.reservedAt).getTime();
            return t >= d.start.getTime() && t <= d.end.getTime();
          })
          .sort((a, b) => new Date(a.reservedAt).getTime() - new Date(b.reservedAt).getTime()),
      ),
    [days, reservationItems],
  );

  const firstEventIdx = useMemo(
    () => dayEvents.findIndex((evs, i) => evs.length > 0 || (dayReservations[i]?.length ?? 0) > 0),
    [dayEvents, dayReservations],
  );

  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isLoading && scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    }
  }, [isLoading]);

  if (isLoading) {
    return (
      <div className="events-gallery">
        {[0, 1, 2].map((n) => (
          <div key={n} className="events-skeleton-card events-skeleton-min" />
        ))}
      </div>
    );
  }

  return (
    <div className="events-gallery">
      {days.map((d, i) => (
        <div
          key={i}
          className="events-gallery-snap"
          ref={i === firstEventIdx ? scrollRef : null}
        >
          <DayColumn
            day={d.date}
            events={dayEvents[i] ?? []}
            reservations={dayReservations[i] ?? []}
            isToday={i === 0}
          />
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Calendar view
// ---------------------------------------------------------------------------

interface CalendarViewProps {
  locationId: string | null;
  month: Date;
  onMonthChange: (month: Date) => void;
  selectedDay: Date | undefined;
  onSelectDay: (day: Date | undefined) => void;
}

function CalendarView({
  locationId,
  month,
  onMonthChange,
  selectedDay,
  onSelectDay,
}: CalendarViewProps): React.ReactElement {
  const { data, isLoading } = useEventsForMonth(
    locationId,
    month.getFullYear(),
    month.getMonth(),
  );
  const items = useMemo(() => data?.items ?? [], [data]);

  const eventsByDay = useMemo(() => {
    const map = new Map<string, CantoEvent[]>();
    for (const event of items) {
      const key = toDateKey(new Date(event.startAt));
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(event);
    }
    return map;
  }, [items]);

  const daysWithEvents = useMemo(
    () => items.map((e) => new Date(e.startAt)),
    [items],
  );

  const selectedDayEvents = useMemo(
    () => (selectedDay ? (eventsByDay.get(toDateKey(selectedDay)) ?? []) : []),
    [selectedDay, eventsByDay],
  );

  return (
    <EventDayContext.Provider value={eventsByDay}>
      <div className="events-calendar-wrapper">
        <div className="events-calendar-container">
          {isLoading && <p className="events-calendar-loading text-muted">Loading…</p>}
          <DayPicker
            mode="single"
            selected={selectedDay}
            onSelect={onSelectDay}
            month={month}
            onMonthChange={onMonthChange}
            modifiers={{ hasEvents: daysWithEvents }}
            modifiersClassNames={{ hasEvents: 'rdp-day--has-events' }}
            components={{ DayButton: CalendarDayButton }}
            showOutsideDays
          />
        </div>

        {selectedDay ? (
          <div style={{ maxWidth: 320 }}>
            <DayColumn day={selectedDay} events={selectedDayEvents} isToday={false} />
          </div>
        ) : !isLoading && items.length === 0 ? (
          <p className="events-empty text-muted">No events this month.</p>
        ) : items.length > 0 ? (
          <p className="events-calendar-hint text-muted">
            Select a highlighted date to see events.
          </p>
        ) : null}
      </div>
    </EventDayContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Custom DayButton — day number + colored dots beneath
// ---------------------------------------------------------------------------

function CalendarDayButton({ day, modifiers: _modifiers, children, ...props }: DayButtonProps): React.ReactElement {
  const eventsByDay = useContext(EventDayContext);
  const dayEvents = eventsByDay.get(toDateKey(day.date)) ?? [];
  const dots = dayEvents.slice(0, 3).map((e) => EVENT_TYPE_CLASS[e.type]);

  return (
    <button {...props}>
      {children}
      {dots.length > 0 && (
        <div className="events-day-dots" aria-hidden>
          {dots.map((dotClass, i) => (
            <span key={i} className={`events-day-dot ${dotClass}`} />
          ))}
        </div>
      )}
    </button>
  );
}

